import React, { useEffect } from 'react';

import { SquarespaceAssets, SquarespaceIndustry, SquarespaceNeeds } from '__generated__/types';
import CheckboxList from 'common/components/CheckboxList';
import Text from 'common/components/Text';
import TextArea from 'common/components/TextArea';
import TextList from 'common/components/TextList';
import { handlePromise } from 'common/utils/Bugsnag';
import SquarespaceIndustryField from 'projects/components/BriefFields/SquarespaceIndustryField';
import SquarespaceNeedsField from 'projects/components/BriefFields/SquarespaceNeedsField';
import ContentSetting from 'projects/components/ContentSetting';
import { ObservableField, ObservableForm } from 'projects/components/ObservableForm';
import { trackViewedBriefEvent } from 'projects/events';
import TimelineField from './TimelineField';

interface Fields {
    title: string;
    needs: SquarespaceNeeds;
    description: string;
    url: string;
    assets: SquarespaceAssets[];
    inspirationUrls: string[];
    timeline: any;
    industry: SquarespaceIndustry;
}

interface Props<T> {
    form: ObservableForm<T>;
    brief: {
        id: string;
    };
}

export const fieldTitles: { [key in keyof Fields]: string } = {
    title: __('What’s the name of your project, business or existing website?'),
    industry: __('What is the industry of your organization?'),
    url: __("If you have an existing website, what's the URL?"),
    needs: __('What do you need help with?'),
    description: __('Please describe the work you do, and what you need.'),
    assets: __('Do you already have any of the following? Select all that apply.'),
    inspirationUrls: __("Are there any websites you'd like to use for inspiration?"),
    timeline: __('Do you have a strict deadline for this project?'),
};

DetailsPage.fields = () => Object.keys(fieldTitles) as (keyof Fields)[];

function DetailsPage<T extends Fields>({ form, brief }: Props<T>) {
    useEffect(() => {
        trackViewedBriefEvent({
            id: brief.id,
            page: 'Details',
        });
    }, []);

    return (
        <ContentSetting>
            <ObservableField
                form={form}
                name="title"
                title={fieldTitles['title']}
                required
                render={() => (
                    <Text
                        placeholder={__("For example: Jane's Bakery")}
                        fill={true}
                        {...form.attach('title')}
                    />
                )}
            />

            <SquarespaceIndustryField title={fieldTitles['industry']} form={form} />

            <ObservableField
                form={form}
                name="url"
                title={fieldTitles['url']}
                render={() => (
                    <Text fill={true} {...form.attach('url')} placeholder="www.example.com" />
                )}
            />

            <SquarespaceNeedsField title={fieldTitles['needs']} form={form} />

            <ObservableField
                form={form}
                name="description"
                required
                title={fieldTitles['description']}
                render={() => (
                    <TextArea
                        style={{ minHeight: 200 }}
                        placeholder={__(
                            'I’m a baker and small business owner. On June 1st, I’m opening a new bakery in Cincinnati, Ohio.\n\nI need an Expert to build a simple but beautiful website with pages for:\n\t-  Our story\n\t-  Location & hours\n\t-  Menu\n\t-  Catering order form\n\t-  Workshop calendar and sign-up'
                        )}
                        fill={true}
                        {...form.attach('description')}
                    />
                )}
            />

            <ObservableField
                form={form}
                name="assets"
                title={fieldTitles['assets']}
                render={() => (
                    <CheckboxList
                        options={[
                            {
                                value: SquarespaceAssets.IMAGES_VIDEO,
                                label: __('Image or Video'),
                            },
                            {
                                value: SquarespaceAssets.COPY,
                                label: __('Written Copy (bios, headlines, about pages, etc.)'),
                            },
                            { value: SquarespaceAssets.LOGO, label: __('Logo and/or Branding') },
                        ]}
                        {...form.attach('assets')}
                    />
                )}
            />

            <ObservableField
                form={form}
                name="inspirationUrls"
                title={fieldTitles['inspirationUrls']}
                render={() => (
                    <TextList {...form.attach('inspirationUrls')} placeholder="www.example.com" />
                )}
            />

            <ObservableField
                form={form}
                name="timeline"
                title={fieldTitles['timeline']}
                render={() => (
                    <TimelineField
                        value={form.get('timeline')}
                        onChange={date => {
                            handlePromise(form.set('timeline', date), 'TimeAndBudgetPage');
                        }}
                    />
                )}
            />
        </ContentSetting>
    );
}

export default DetailsPage;
